import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Collapse } from '@mui/material'
import { useFormikContext } from 'formik'
import React, { useState } from 'react'
import { BlankView } from 'src/Components/blank-view'
import { SettingsBox } from 'src/Elements/Data'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { TextField } from 'src/Libs/inputs/formik'
import { WallboxSettingsFormModel } from 'src/Libs/wallboxes/models/wallbox-configuration/wallbox-configuration-settings-form.model'

interface Props {
  disabled?: boolean
  error?: Error | null
}

/**
 * OCPP related settings
 */
const OcppFieldSet: React.FC<Props> = ({ disabled = false, error }) => {
  const { t } = useCustomTranslation()
  const { values, setFieldValue } = useFormikContext<WallboxSettingsFormModel>()
  const [isPasswordFieldDisabled, setIsPasswordFieldDisabled] = useState(true)
  const isOcppEnabled = values?.ocppEnabled ?? false
  const isBasicAuthentication = values.ocppAuthType ?? false
  const passwordEditorButtonText = isPasswordFieldDisabled ? t('common.edit') : t('common.cancel')

  const togglePasswordEditor = (): void => {
    const isPasswordFieldDisabledAfterToggle = !isPasswordFieldDisabled
    if (isPasswordFieldDisabledAfterToggle) setFieldValue('ocppPassword', '')
    setIsPasswordFieldDisabled(isPasswordFieldDisabledAfterToggle)
  }

  if (error)
    return (
      <OcppFieldSetSection>
        <BlankView
          title="systemSettings.api.no-data-available.title"
          explanation="systemSettings.api.no-data-available.explanation"
          imageVariant="WORRIED"
          imageSize="SMALL"
        />
      </OcppFieldSetSection>
    )

  return (
    <OcppFieldSetSection>
      <Box>
        <SingleToggleSettingBox title={t('systemSettings.wallbox.settings.ocpp.enabled')} name="ocppEnabled" disabled={disabled} />
        <Collapse in={isOcppEnabled} timeout="auto" unmountOnExit>
          <SettingsBox
            disabled={disabled}
            title={t('systemSettings.wallbox.settings.ocpp.server-address')}
            suffix={<TextField name="ocppServerAddress" disabled={disabled} label={t('systemSettings.wallbox.settings.ocpp.server-address')} />}
          />
          <SettingsBox
            disabled={disabled}
            title={t('systemSettings.wallbox.settings.ocpp.id')}
            suffix={<TextField name="ocppId" disabled={disabled} label={t('systemSettings.wallbox.settings.ocpp.id')} />}
          />
          <SingleToggleSettingBox title={t('systemSettings.wallbox.settings.ocpp.auth-type')} name="ocppAuthType" disabled={disabled} />
          <Collapse in={isBasicAuthentication} timeout="auto" unmountOnExit>
            <SettingsBox
              disabled={disabled}
              title={t('systemSettings.wallbox.settings.ocpp.password.title')}
              description={t('systemSettings.wallbox.settings.ocpp.password.description')}
              suffix={
                <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    name="ocppPassword"
                    type="password"
                    disabled={disabled || isPasswordFieldDisabled}
                    autoComplete="new-password"
                    label={t('systemSettings.wallbox.settings.ocpp.password.title')}
                  />
                  <Button variant="text" onClick={togglePasswordEditor}>
                    {passwordEditorButtonText}
                  </Button>
                </Box>
              }
            />
          </Collapse>
          <SingleToggleSettingBox
            title={t('systemSettings.wallbox.settings.ocpp.allow-local-charge')}
            name="ocppAllowLocalCharge"
            disabled={disabled}
          />
        </Collapse>
      </Box>
    </OcppFieldSetSection>
  )
}

interface LayoutProps {
  children: React.ReactElement
}

/**
 * Ocpp field section with title. Content is passed as children.
 *
 * @returns
 */
const OcppFieldSetSection: React.FC<LayoutProps> = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h2">{'systemSettings.wallbox.settings.ocpp.title'}</Typography>
      {children}
    </Box>
  )
}

export default OcppFieldSet
